import type { LucideIcon } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

/**
 * Router-agnostic link component. Both `next/link` and wouter's `Link` satisfy
 * this shape (they accept `href` + children and forward `className`/`onClick`
 * to an underlying anchor), so the shared shell can render either.
 */
export interface NavLinkProps {
	href: string;
	className?: string;
	children: ReactNode;
	onClick?: () => void;
	title?: string;
}

export type NavLinkComponent = ComponentType<NavLinkProps>;

/** Nested entry rendered as a SidebarMenuSub row under its parent NavItem. */
export type NavSubItem = Pick<NavItem, "href" | "label" | "exact" | "hidden">;

export interface NavItem {
	href: string;
	label: string;
	icon?: LucideIcon;
	/** Match exactly — don't highlight when the location is a child of `href`. */
	exact?: boolean;
	/** Show the shell-level indicator dot on this item (e.g. cluster dirty). */
	indicator?: boolean;
	/** Hide the item entirely (e.g. role-gated). */
	hidden?: boolean;
	/**
	 * Sub-menu entries. Rendered as a collapsible SidebarMenuSub list with a
	 * chevron toggle (hidden entirely in icon-collapsed mode). Collapsed by
	 * default; forced open while the location matches the parent or any
	 * child. The parent stays a link and highlights on itself or any child.
	 */
	items?: NavSubItem[];
}

export interface NavSection {
	label?: string;
	items: NavItem[];
}

export interface ShellUser {
	name: string;
	/** Secondary line: email, role, etc. */
	subtitle?: ReactNode;
	image?: string | null;
	/** Explicit avatar initials; derived from `name` when omitted. */
	initials?: string;
}

export interface ShellBrand {
	name: string;
	subtitle?: ReactNode;
	/** Rendered inside the brand badge box (icon/logo). */
	icon?: ReactNode;
	/** Brand link target (default "/"). */
	href?: string;
}

/** Match logic shared by the sidebar and breadcrumbs. */
export function isNavActive(
	pathname: string,
	href: string,
	exact?: boolean,
): boolean {
	if (href === "/") return pathname === "/";
	if (exact) return pathname === href;
	return pathname === href || pathname.startsWith(`${href}/`);
}

export function deriveInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
	return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
